import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, setUnauthorizedHandler } from "../lib/api.js";
import type { MeResponse } from "../lib/types.js";

/**
 * "checking" is de korte periode waarin we bij de server navragen of het
 * sessiecookie nog geldig is. De app toont dan niets: een loginformulier dat na
 * een halve seconde weer verdwijnt is erger dan even wachten.
 */
type AuthStatus = "checking" | "anonymous" | "authenticated";

interface AuthValue {
  status: AuthStatus;
  me: MeResponse | null;
  login: (address: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [me, setMe] = useState<MeResponse | null>(null);

  const forget = useCallback(() => {
    setMe(null);
    setStatus("anonymous");
  }, []);

  // Elke 401 uit welke aanroep dan ook betekent dat de sessie weg is; dan hoort
  // de hele app terug naar het loginscherm.
  useEffect(() => {
    setUnauthorizedHandler(forget);
    return () => {
      setUnauthorizedHandler(null);
    };
  }, [forget]);

  useEffect(() => {
    let cancelled = false;
    api
      .me(true)
      .then((value) => {
        if (cancelled) return;
        setMe(value);
        setStatus("authenticated");
      })
      .catch(() => {
        if (!cancelled) forget();
      });
    return () => {
      cancelled = true;
    };
  }, [forget]);

  const login = useCallback(async (address: string, password: string, remember = false) => {
    const value = await api.login(address, password, remember);
    setMe(value);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Ook als de server al niets meer van ons weet: lokaal is de sessie weg.
      forget();
    }
  }, [forget]);

  const value = useMemo<AuthValue>(
    () => ({ status, me, login, logout }),
    [status, me, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error("useAuth() buiten een AuthProvider gebruikt");
  return value;
}
